/**
 * Global fetch interceptor: attaches the Firebase ID token as a Bearer header
 * to every request aimed at our backend (REST + fetch-based SSE streams alike).
 *
 * Installing it once (from index.js) means none of the ~30 APIService calls or
 * the streaming `/chat` readers need to know about auth — they just work. The
 * only backend call that can't use this is the voice WebSocket, which carries
 * the token as a `?token=` query param instead (see VoiceInterviewInterface).
 */
import { auth } from './firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

let installed = false;

export function installAuthFetch() {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const isBackend = url.startsWith(API_BASE_URL) || url.startsWith('/');

    if (isBackend && auth.currentUser) {
      try {
        const token = await auth.currentUser.getIdToken();
        const headers = new Headers(
          init.headers || (typeof input !== 'string' ? input.headers : undefined),
        );
        if (!headers.has('Authorization')) {
          headers.set('Authorization', `Bearer ${token}`);
        }
        init = { ...init, headers };
      } catch {
        /* fall through unauthenticated — the backend will 401 */
      }
    }
    return originalFetch(input, init);
  };
}
